import { getPremiumTtsClientId } from "./premiumTts.js";

const apiBaseUrl = import.meta.env.VITE_SIELPA_PLUS_API_BASE_URL;
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
const appVersion = import.meta.env.VITE_SIELPA_APP_VERSION || import.meta.env.VITE_APP_VERSION || "";

export const PLUS_PLANS = {
  monthly: {
    id: "monthly",
    label: "Monthly",
    price: "¥300/month",
    lookupKey: import.meta.env.VITE_STRIPE_PLUS_MONTHLY_LOOKUP_KEY || "sielpa_plus_monthly"
  },
  yearly: {
    id: "yearly",
    label: "Yearly",
    price: "¥2,400/year",
    lookupKey: import.meta.env.VITE_STRIPE_PLUS_YEARLY_LOOKUP_KEY || "sielpa_plus_yearly"
  }
};

function normalizedApiBaseUrl() {
  return String(apiBaseUrl || "").replace(/\/$/, "");
}

export function isBillingConfigured() {
  return Boolean(apiBaseUrl && anonKey);
}

function getCurrentAppUrl(status) {
  const url = new URL(window.location.href);
  url.searchParams.set("sielpa_plus", status);
  if (!url.hash) url.hash = "#/";
  return url.toString();
}

function createBillingError(code, message, data = {}) {
  const error = new Error(message);
  error.code = code;
  error.data = data;
  return error;
}

async function postBillingEndpoint(endpoint, payload) {
  if (!isBillingConfigured()) {
    throw createBillingError(
      "billing_not_configured",
      "SielpA Plus checkout is not connected yet. Add the Supabase function URL and anon key to the React environment."
    );
  }

  let response;
  try {
    response = await fetch(`${normalizedApiBaseUrl()}/${endpoint}`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${anonKey}`,
        apikey: anonKey,
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        app: "sielpa",
        clientId: getPremiumTtsClientId(),
        ...(appVersion ? { appVersion } : {}),
        ...payload
      })
    });
  } catch (error) {
    throw createBillingError(
      "billing_network_error",
      "SielpA Plus checkout could not connect. The payment backend may not be deployed yet.",
      { cause: error }
    );
  }

  const data = await response.json().catch(() => ({}));
  if (!response.ok || data.ok === false) {
    throw createBillingError(
      data.code || (response.status === 404 ? "billing_endpoint_missing" : "billing_request_failed"),
      data.message || "SielpA Plus checkout is not ready yet.",
      data
    );
  }

  return data;
}

export async function createCheckoutSession({ interval = "monthly", source = "app", ...trace } = {}) {
  const plan = PLUS_PLANS[interval] || PLUS_PLANS.monthly;
  const data = await postBillingEndpoint("create-plus-checkout", {
    plan: "sielpa_plus",
    interval: plan.id,
    priceLookupKey: plan.lookupKey,
    source,
    ...trace,
    successUrl: getCurrentAppUrl("success"),
    cancelUrl: getCurrentAppUrl("canceled")
  });

  const url = data.url || data.checkoutUrl;
  if (!url) {
    throw createBillingError("checkout_url_missing", "Checkout opened, but Stripe did not return a checkout URL.", data);
  }

  return { ...data, url, plan };
}

export async function createCustomerPortalSession({ source = "app" } = {}) {
  const data = await postBillingEndpoint("create-plus-portal", {
    source,
    returnUrl: getCurrentAppUrl("portal")
  });

  const url = data.url || data.portalUrl;
  if (!url) {
    throw createBillingError("portal_url_missing", "The billing portal did not return a URL.", data);
  }

  return { ...data, url };
}

export async function fetchPlusStatus({ source = "app" } = {}) {
  const data = await postBillingEndpoint("get-plus-status", { source });
  return {
    ...data,
    isPlus: Boolean(data.isPlus || data.tier === "plus" || data.entitlements?.plus),
    isAdFree: Boolean(data.isAdFree || data.isPlus || data.tier === "plus" || data.entitlements?.adFree),
    hasBillingCustomer: Boolean(data.hasBillingCustomer)
  };
}
